import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

interface PageHeaderContextValue {
  /** Current measured height of the PageHeader in pixels */
  pageHeaderHeight: number;
  /** Whether the PageHeader is currently visible (not hidden by scroll) */
  isPageHeaderVisible: boolean;
  /** Called by PageHeader to report its measured height */
  setPageHeaderHeight: (height: number) => void;
  /** Called by PageHeader to report its visibility */
  setPageHeaderVisible: (visible: boolean) => void;
}

const PageHeaderContext = createContext<PageHeaderContextValue>({
  pageHeaderHeight: 0,
  isPageHeaderVisible: true,
  setPageHeaderHeight: () => {},
  setPageHeaderVisible: () => {},
});

export const PageHeaderProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [pageHeaderHeight, setPageHeaderHeight] = useState(0);
  const [isPageHeaderVisible, setPageHeaderVisible] = useState(true);

  const setHeight = useCallback((h: number) => setPageHeaderHeight(h), []);
  const setVisible = useCallback((v: boolean) => setPageHeaderVisible(v), []);

  const value = useMemo(() => ({
    pageHeaderHeight,
    isPageHeaderVisible,
    setPageHeaderHeight: setHeight,
    setPageHeaderVisible: setVisible,
  }), [pageHeaderHeight, isPageHeaderVisible, setHeight, setVisible]);

  return (
    <PageHeaderContext.Provider value={value}>
      {children}
    </PageHeaderContext.Provider>
  );
};

export function usePageHeader(): PageHeaderContextValue {
  return useContext(PageHeaderContext);
}
